import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { installTawkTelemetryGuard } from "utils/tawk-telemetry-guard";
import {
  initializeTawkTo,
  removeTawkTo,
  shouldEnableTawk,
} from "utils/tawkto";

/**
 * Mounts Tawk.to only on routes allowed by shouldEnableTawk()
 * (marketing, support, legal, login, activation landing). Removes the widget when
 * navigating to purchase / recharge / activation flow steps / account / agent flows.
 */
export const TawkToHandler = () => {
  const location = useLocation();
  const isAllowedRef = useRef(false);

  // Keep a single onLoad hook that respects the latest route allow/deny state
  useEffect(() => {
    installTawkTelemetryGuard();
    window.Tawk_API = window.Tawk_API || {};

    // Set up onLoad handler once, ensuring it relies on the current ref value
    if (!window.Tawk_API._isCustomOnLoadSet) {
      const originalOnLoad = window.Tawk_API.onLoad;
      window.Tawk_API.onLoad = () => {
        if (originalOnLoad) originalOnLoad();
        if (!isAllowedRef.current) {
          removeTawkTo();
        } else {
          window.Tawk_API?.showWidget?.();
        }
      };
      window.Tawk_API._isCustomOnLoadSet = true;
    }

    // Fail-safe for returning to a throttled or background tab
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!isAllowedRef.current) {
        removeTawkTo();
      } else {
        window.Tawk_API?.showWidget?.();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const allowed = shouldEnableTawk(location.pathname);
    isAllowedRef.current = allowed;

    if (!allowed) {
      removeTawkTo();
      return;
    }

    initializeTawkTo();

    // Re-attach onLoad after removeTawkTo resets Tawk_API to {}
    window.Tawk_API = window.Tawk_API || {};
    if (!window.Tawk_API._isCustomOnLoadSet) {
      window.Tawk_API.onLoad = () => {
        if (!isAllowedRef.current) {
          removeTawkTo();
        } else {
          window.Tawk_API?.showWidget?.();
        }
      };
      window.Tawk_API._isCustomOnLoadSet = true;
    }

    window.Tawk_API?.showWidget?.();
  }, [location.pathname]);

  return null;
};
